import { IconDefinition } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation } from '@tanstack/react-query'
import React, { useContext } from 'react'
import { Alert } from 'react-bootstrap'

import ActionButton from '../../../../components/misc/ActionButton'
import OpenChatButton from '../../../../components/misc/OpenChatButton'
import { SubscriptionType } from '../../../../enums/SubscriptionType'
import useNotyf from '../../../../hooks/useNotyf'
import useSubscription from '../../../../hooks/useSubscription'
import { NylasRedirect } from '../../../../lib/constants'
import apiService from '../../../../lib/services/apiService'
import { INTEGRATION_SERVICE, IntegrationService } from '../../../../types/misc'
import NylasRedirectContext from './NylasRedirectContext'

interface Props {
  text: string
  integratedService: IntegrationService
  icon: IconDefinition
  disabled?: boolean
  className?: string
}

const ConnectUserAccountButton: React.FC<Props> = ({
  text,
  integratedService,
  icon,
  disabled,
  className = null,
}) => {
  const notyf = useNotyf()
  const nylasRedirect = useContext<NylasRedirect>(NylasRedirectContext)

  function getSubscriptionType() {
    if (integratedService === INTEGRATION_SERVICE.nylas) {
      return SubscriptionType.SharedEmailIntegration
    }

    if (integratedService === INTEGRATION_SERVICE.nylasEmail) {
      return SubscriptionType.PersonalEmailIntegration
    }

    if (integratedService === INTEGRATION_SERVICE.nylasCalendar) {
      return SubscriptionType.CalendarIntegration
    }

    throw `Unexpected integrated service ${integratedService}`
  }

  const subscription = useSubscription(getSubscriptionType())

  const buildAuthUrlMutation = useMutation({
    mutationFn: async (): Promise<string> => {
      const response = await apiService.post(
        `/v1/nylas/build-auth-url/${nylasRedirect.toString()}`,
        { integratedService: integratedService },
      )

      return response.data.hostedAuthUrl
    },
    onSuccess: (hostedAuthUrl: string) => {
      // Now hand over to Nylas to do the auth, and redirect back to us at the end
      window.location.href = hostedAuthUrl
    },
    onError: () => {
      notyf.error(
        'Something went wrong. Please try connecting your account again.',
      )
    },
  })

  /**
   * @see https://developer.nylas.com/docs/v3/auth/hosted-oauth-accesstoken/#make-authorization-request
   */
  function beginHostedAuthFlow() {
    buildAuthUrlMutation.mutate()
  }

  if (subscription !== undefined && !subscription.active) {
    return (
      <Alert variant="warning" className="p-3 m-0">
        <p className="m-0">
          Your subscription for this feature has ended. Please{' '}
          <OpenChatButton>get in touch</OpenChatButton> if you'd like to
          continue.
        </p>
      </Alert>
    )
  }

  return (
    <ActionButton
      isProcessing={buildAuthUrlMutation.isPending}
      onClick={beginHostedAuthFlow}
      size="lg"
      disabled={disabled}
      className={className || 'align-self-start'}
    >
      <FontAwesomeIcon icon={icon} className="me-2" /> {text}
    </ActionButton>
  )
}

export default ConnectUserAccountButton
